import { Title, Text } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Box, Stack, TextField } from "@mui/material";
import type { WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import type { UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { useUpdateInstantBook } from "@src/appV2/FacilityDrawer/InstantBook/api/useUpdateInstantBook";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { FACILITY_ROLES } from "@src/constants/facility";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import type { SessionType } from "@src/modules/interface";
import axios from "axios";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SettingsItem } from "./SettingsItem";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { LateCancellationCallNotificationSetting } from "./LateCancellationCallNotificationSetting";

const CONSECUTIVE_HOURS_LIMIT_MINIMUM = 8;
const CONSECUTIVE_HOURS_LIMIT_DEFAULT = 8;

const WEEKLY_WORK_HOURS_LIMIT_MINIMUM = 8;
const WEEKLY_WORK_HOURS_LIMIT_DEFAULT = 40;
const WEEKLY_WORK_HOURS_LIMIT_MAXIMUM = 168;

interface ScheduleSettingsProps {
  facilityProfile: UseGetFacilityProfileResponse & {
    instantBook?: boolean | string;
    requestToBook?: {
      autoApprovePreviousApprovedWorkers?: boolean;
      qualifications?: string[];
      shiftTimeSlots?: string[];
    };
  };
  workplace: WorkplaceProfileResponse;
  onSettingsChanged: () => void;
}

interface UpdateMaxHoursRestrictionParams {
  field: "maxAllowedWorkConsecutiveHours" | "maxAllowedWorkHoursPerWeek";
  hours: number | null;
  debounce?: boolean;
}

export function ScheduleSettings(props: ScheduleSettingsProps) {
  const { facilityProfile, workplace, onSettingsChanged } = props;
  const session = useSession();
  const adminRoles = session?.adminProfile?.roles ?? [];
  const hasRequestToBookPermission = adminRoles.includes(EMPLOYEE_ROLES.REQUEST_TO_BOOK);

  const [maxHoursRestriction, setMaxHoursRestriction] = useState<{
    blockDoubleShifts: boolean;
    maxAllowedWorkConsecutiveHours: number | null;
    maxAllowedWorkHoursPerWeek: number | null;
  }>({
    blockDoubleShifts:
      isDefined(facilityProfile.maxAllowedWorkConsecutiveHours) &&
      facilityProfile.maxAllowedWorkConsecutiveHours > 0,
    maxAllowedWorkConsecutiveHours: facilityProfile.maxAllowedWorkConsecutiveHours ?? null,
    maxAllowedWorkHoursPerWeek: facilityProfile.maxAllowedWorkHoursPerWeek ?? null,
  });

  const { showSuccessToast } = useToast();
  const { user, admin, profile } = useSelector((state: SessionType) => state.session);
  const { isLoading: isFacilityProfileLoading, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();
  const { isLoading: isInstantBookLoading, mutateAsync: updateInstantBook } =
    useUpdateInstantBook();
  const isLoading = isFacilityProfileLoading || isInstantBookLoading;
  const canUpdateMaxConsecutiveHours =
    admin ||
    (isDefined(user)
      ? [FACILITY_ROLES.ADMIN, FACILITY_ROLES.SHIFT_MANAGEMENT].some((role) =>
          user.access?.includes(role)
        )
      : false);

  const shouldRebrandMaxConsecutiveHoursToNoDoubles = useCbhFlag(
    CbhFeatureFlag.REBRAND_MAX_CONSECUTIVE_HOURS_TO_NO_DOUBLES,
    { defaultValue: false }
  );

  const consecutiveHoursInputRef = useRef<HTMLInputElement>(null);
  const weeklyHoursInputRef = useRef<HTMLInputElement>(null);

  const isRequestToBookEnabled =
    typeof facilityProfile.instantBook === "string" &&
    facilityProfile.instantBook === "REQUEST_TO_BOOK";

  const [requestToBookState, setRequestToBookState] = useState({
    enabled: isRequestToBookEnabled,
    autoApprovePreviousApprovedWorkers:
      facilityProfile.requestToBook?.autoApprovePreviousApprovedWorkers ?? false,
  });

  const updateRequestToBook = async (enabled: boolean) => {
    setRequestToBookState((prevState) => ({
      ...prevState,
      enabled,
    }));

    try {
      await updateInstantBook({
        workplaceId: facilityProfile.userId,
        data: {
          instantBook: enabled ? "REQUEST_TO_BOOK" : "ON",
          requestToBook: {
            autoApprovePreviousApprovedWorkers: enabled
              ? requestToBookState.autoApprovePreviousApprovedWorkers
              : false,
            qualifications: [],
            shiftTimeSlots: [],
          },
        },
      });

      showSuccessToast("Request to Book settings updated successfully");
      onSettingsChanged();
    } catch (error) {
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast(
          "Error updating workplace's profile. Please, try again later or contact support"
        );
      }
    }
  };

  const updateAutoApprovePreviousApprovedWorkers = async (enabled: boolean) => {
    setRequestToBookState((prevState) => ({
      ...prevState,
      autoApprovePreviousApprovedWorkers: enabled,
    }));

    try {
      await updateInstantBook({
        workplaceId: facilityProfile.userId,
        data: {
          instantBook: "REQUEST_TO_BOOK",
          requestToBook: {
            autoApprovePreviousApprovedWorkers: enabled,
            qualifications: facilityProfile.requestToBook?.qualifications ?? [],
            shiftTimeSlots: facilityProfile.requestToBook?.shiftTimeSlots ?? [],
          },
        },
      });

      showSuccessToast("Auto-approve settings updated successfully");
      onSettingsChanged();
    } catch (error) {
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast(
          "Error updating workplace's profile. Please, try again later or contact support"
        );
      }
    }
  };

  const updateMaxHoursRestriction = async ({ field, hours }: UpdateMaxHoursRestrictionParams) => {
    const previousValue = maxHoursRestriction[field];

    if (!admin && user) {
      logEvent(HCF_USER_EVENTS.MAX_HOURS_RESTRICTION, {
        workplaceId: profile.userId,
        workplaceUserId: user._id,
        field,
        value: hours,
        previousValue,
      });
    }

    if (
      field === "maxAllowedWorkConsecutiveHours" &&
      isDefined(hours) &&
      hours < CONSECUTIVE_HOURS_LIMIT_MINIMUM &&
      !shouldRebrandMaxConsecutiveHoursToNoDoubles
    ) {
      showErrorToast(`Restrictions must be no less than ${CONSECUTIVE_HOURS_LIMIT_MINIMUM} hours`);
      if (consecutiveHoursInputRef.current) {
        consecutiveHoursInputRef.current.value =
          previousValue?.toString() ?? CONSECUTIVE_HOURS_LIMIT_DEFAULT.toString();
      }
      return;
    }

    if (
      field === "maxAllowedWorkHoursPerWeek" &&
      isDefined(hours) &&
      hours < WEEKLY_WORK_HOURS_LIMIT_MINIMUM
    ) {
      showErrorToast(
        `Weekly hours limit must be no less than ${WEEKLY_WORK_HOURS_LIMIT_MINIMUM} hours`
      );
      if (weeklyHoursInputRef.current && previousValue) {
        weeklyHoursInputRef.current.value = previousValue.toString();
      }
      return;
    }

    if (
      field === "maxAllowedWorkHoursPerWeek" &&
      isDefined(hours) &&
      hours > WEEKLY_WORK_HOURS_LIMIT_MAXIMUM
    ) {
      showErrorToast(
        `Weekly hours limit must be no more than ${WEEKLY_WORK_HOURS_LIMIT_MAXIMUM} hours`
      );
      if (weeklyHoursInputRef.current && previousValue) {
        weeklyHoursInputRef.current.value = previousValue.toString();
      }
      return;
    }

    setMaxHoursRestriction((prevState) => ({
      ...prevState,
      [field]: hours,
    }));

    try {
      await updateFacilityProfile({
        facilityId: facilityProfile.userId,
        [field]: hours,
      });

      showSuccessToast("Schedule preferences updated successfully");
      onSettingsChanged();
    } catch (error) {
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast(
          "Error updating workplace's profile. Please, try again later or contact support"
        );
      }
    }
  };

  const createConsecutiveHoursTitle = () => {
    if (shouldRebrandMaxConsecutiveHoursToNoDoubles) {
      return "Prevent professionals from booking double shifts";
    }

    return (
      <>
        <Text display="inline">Block professionals from booking more than</Text>
        <TextField
          inputRef={consecutiveHoursInputRef}
          inputProps={{
            style: { padding: 4, textAlign: "center" },
            type: "number",
            className: "input-number",
            "aria-label": "Max consecutive hours per 24h",
          }}
          onBlur={(event) => {
            const hours = Number(event.target.value);
            if (
              !Number.isNaN(hours) &&
              hours !== maxHoursRestriction.maxAllowedWorkConsecutiveHours
            ) {
              void updateMaxHoursRestriction({
                field: "maxAllowedWorkConsecutiveHours",
                hours,
              });
            }
          }}
          name="maxAllowedWorkConsecutiveHours"
          sx={{ width: 48, height: 24, display: "inline-block", mx: 1 }}
          disabled={isLoading || !isDefined(maxHoursRestriction.maxAllowedWorkConsecutiveHours)}
          defaultValue={
            maxHoursRestriction.maxAllowedWorkConsecutiveHours ?? CONSECUTIVE_HOURS_LIMIT_DEFAULT
          }
        />
        <Text display="inline">hours of consecutive work</Text>
      </>
    );
  };

  const createWeeklyHoursTitle = () => (
    <>
      <Text display="inline">
        {shouldRebrandMaxConsecutiveHoursToNoDoubles ? "Prevent" : "Block"} professionals from
        booking more than
      </Text>
      <TextField
        inputRef={weeklyHoursInputRef}
        inputProps={{
          style: { padding: 4, textAlign: "center" },
          type: "number",
          className: "input-number",
          "aria-label": "Max consecutive hours per week",
        }}
        name="maxAllowedWorkHoursPerWeek"
        sx={{ width: 48, height: 24, display: "inline-block", mx: 1 }}
        onBlur={(event) => {
          const hours = Number(event.target.value);
          if (!Number.isNaN(hours) && hours !== maxHoursRestriction.maxAllowedWorkHoursPerWeek) {
            void updateMaxHoursRestriction({
              field: "maxAllowedWorkHoursPerWeek",
              hours,
            });
          }
        }}
        disabled={isLoading || !isDefined(maxHoursRestriction.maxAllowedWorkHoursPerWeek)}
        defaultValue={
          maxHoursRestriction.maxAllowedWorkHoursPerWeek ?? WEEKLY_WORK_HOURS_LIMIT_DEFAULT
        }
      />
      <Text display="inline">hours of work per week (Sun - Sat)</Text>
    </>
  );

  const requestToBookSubtext =
    !admin || !hasRequestToBookPermission
      ? "To modify the workplace's Request to Book setting, submit a request in #proj-request-to-book"
      : undefined;

  return canUpdateMaxConsecutiveHours ? (
    <Stack position="relative" pl={2} sx={{ opacity: isLoading ? 0.5 : 1 }}>
      <Title component="h3">Schedule Preferences</Title>

      {isLoading ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}

      {admin ? (
        <>
          <SettingsItem
            item={{
              key: "requestToBook",
              title: "Enable Request to Book",
              subtitle: requestToBookSubtext,
              checked: requestToBookState.enabled,
              onChange: (checked: boolean) => {
                void updateRequestToBook(checked);
              },
              loading: isLoading,
              disabled: !hasRequestToBookPermission,
            }}
            isLoading={isLoading}
          />

          {requestToBookState.enabled && (
            <SettingsItem
              item={{
                key: "autoApprovePreviousApprovedWorkers",
                title: "Enable Instant Book for previously approved professionals",
                subtitle: requestToBookSubtext,
                checked: requestToBookState.autoApprovePreviousApprovedWorkers,
                onChange: (checked: boolean) => {
                  void updateAutoApprovePreviousApprovedWorkers(checked);
                },
                loading: isLoading,
                disabled: !hasRequestToBookPermission,
              }}
              isLoading={isLoading}
            />
          )}
        </>
      ) : null}

      <SettingsItem
        item={{
          key: "maxAllowedWorkConsecutiveHours",
          title: createConsecutiveHoursTitle(),
          checked: isDefined(maxHoursRestriction.maxAllowedWorkConsecutiveHours),
          onChange: (checked) => {
            let hours = CONSECUTIVE_HOURS_LIMIT_DEFAULT;
            if (consecutiveHoursInputRef.current && !shouldRebrandMaxConsecutiveHoursToNoDoubles) {
              hours = Number(consecutiveHoursInputRef.current.value);
            } else if (shouldRebrandMaxConsecutiveHoursToNoDoubles) {
              hours = 1;
            }
            void updateMaxHoursRestriction({
              field: "maxAllowedWorkConsecutiveHours",
              hours: checked ? hours : null,
            });
          },
          loading: isLoading,
        }}
        isLoading={isLoading}
      />

      <SettingsItem
        item={{
          key: "maxAllowedWorkHoursPerWeek",
          title: createWeeklyHoursTitle(),
          checked: isDefined(maxHoursRestriction.maxAllowedWorkHoursPerWeek),
          onChange: (checked) => {
            let hours = WEEKLY_WORK_HOURS_LIMIT_DEFAULT;
            if (weeklyHoursInputRef.current) {
              hours = Number(weeklyHoursInputRef.current.value);
            }
            void updateMaxHoursRestriction({
              field: "maxAllowedWorkHoursPerWeek",
              hours: checked ? hours : null,
            });
          },
          loading: isLoading,
        }}
        isLoading={isLoading}
      />

      {admin && (
        <LateCancellationCallNotificationSetting
          workplace={workplace}
          onPreferencesChanged={onSettingsChanged}
        />
      )}
    </Stack>
  ) : null;
}
